import { Button, Input, Select } from '../../../design-system';
import { DerivedStateField } from '../../../components/StateSelect';
import { stateCodeFromGstin, stateNameByCode } from '../../../lib/indiaStates';
import AddressFields, { emptyAddress } from './AddressFields';

export const REGISTRATION_TYPES = [
  'Regular', 'Composition', 'Casual', 'NRTP', 'SEZ_Unit', 'SEZ_Developer', 'ISD', 'TDS', 'TCS',
];
export const GSTIN_STATUSES = ['Active', 'Cancelled', 'Suspended', 'Non_Filing'];

export const emptyGstin = () => ({
  gstin: '', stateCode: '', registrationDate: '', registrationType: 'Regular', filingFrequency: 'Monthly',
  status: 'Active', cancellationDate: '', centralJurisdiction: '', stateJurisdiction: '',
  arn: '', provisionalId: '', ppob: emptyAddress(), apob: [],
});

// The full controlled field set for a single GSTIN, shared by the detail-page
// modal (GstinManager) and the Add Client form. `value` is one gstin object,
// `onChange` receives the updated copy.
//
// `showArnFields` — ARN and Provisional ID belong to a registration that is
// still in flight, which is not the case for a client being onboarded with a
// GSTIN already in hand. The Add Client form hides them; the detail modal keeps
// them so an in-flight registration can still be recorded later.
function GstinFields({ value, onChange, showArnFields = true }) {
  const set = (field, v) => onChange({ ...value, [field]: v });
  const apob = value.apob || [];

  // ⚠️ The state code is DERIVED from the GSTIN, never typed beside it. Digits
  // 1-2 of a GSTIN *are* the state code, so a second editable field for it was
  // a second answer to a question the number had already answered — and the two
  // could disagree, which decides the wrong tax head on every invoice to that
  // registration. Typing the GSTIN now sets the code, and fills the PPOB state
  // if that hasn't been answered yet (never overwriting a state already chosen).
  const setGstin = (raw) => {
    const gstin = raw.toUpperCase();
    const stateCode = stateCodeFromGstin(gstin);
    const next = { ...value, gstin, stateCode };
    const ppob = value.ppob || emptyAddress();
    if (stateCode && !ppob.stateCode) {
      next.ppob = { ...ppob, stateCode, state: stateNameByCode(stateCode) };
    }
    onChange(next);
  };

  const addApob = () => set('apob', [...apob, emptyAddress()]);
  const setApob = (idx, addr) => set('apob', apob.map((a, i) => (i === idx ? addr : a)));
  const removeApob = (idx) => set('apob', apob.filter((_, i) => i !== idx));

  return (
    <>
      <div className="form-row">
        <Input label="GSTIN *" value={value.gstin} onChange={(e) => setGstin(e.target.value)} maxLength={15} />
        <DerivedStateField code={value.stateCode} emptyHint="From the GSTIN" />
        <Input label="Registration Date" type="date" value={value.registrationDate || ''} onChange={(e) => set('registrationDate', e.target.value)} />
      </div>
      <div className="form-row">
        <Select label="Registration Type" value={value.registrationType} onChange={(e) => set('registrationType', e.target.value)}>
          {REGISTRATION_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </Select>
        <Select label="Filing Frequency (QRMP)" value={value.filingFrequency} onChange={(e) => set('filingFrequency', e.target.value)}>
          <option value="Monthly">Monthly</option>
          <option value="Quarterly">Quarterly (QRMP)</option>
        </Select>
        <Select label="Status" value={value.status} onChange={(e) => set('status', e.target.value)}>
          {GSTIN_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </Select>
        {value.status !== 'Active' && (
          <Input label="Cancellation Date" type="date" value={value.cancellationDate || ''} onChange={(e) => set('cancellationDate', e.target.value)} />
        )}
      </div>
      <div className="form-row">
        <Input label="Central Jurisdiction" value={value.centralJurisdiction} onChange={(e) => set('centralJurisdiction', e.target.value)} />
        <Input label="State Jurisdiction" value={value.stateJurisdiction} onChange={(e) => set('stateJurisdiction', e.target.value)} />
      </div>
      {showArnFields && (
        <div className="form-row">
          <Input label="ARN (in-flight registration)" value={value.arn} onChange={(e) => set('arn', e.target.value)} />
          <Input label="Provisional ID" value={value.provisionalId} onChange={(e) => set('provisionalId', e.target.value)} />
        </div>
      )}

      <AddressFields legend="Principal Place of Business (PPOB)" value={value.ppob || emptyAddress()} onChange={(v) => set('ppob', v)} />

      <div className="apob-header">
        <span className="ds-field-label">Additional Places of Business (APOB)</span>
        <Button type="button" variant="secondary" size="sm" onClick={addApob}>+ Add APOB</Button>
      </div>
      {apob.map((addr, idx) => (
        <div key={idx} className="apob-row">
          <AddressFields legend={`APOB ${idx + 1}`} value={addr} onChange={(v) => setApob(idx, v)} />
          <button type="button" className="btn-remove-icon" onClick={() => removeApob(idx)}>×</button>
        </div>
      ))}
    </>
  );
}

export default GstinFields;
